import React from "react";
import { Box, Sheet } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import type {
  SMSPlaceholderDefinition,
  SMSTemplate,
} from "../../../../api/smsTemplates/SMSTemplate.typed";
import { SMSPlaceholderChips } from "../../../shared/sms/SMSPlaceholderChips";
import CuratedEmojiPicker from "../../../common/CuratedEmojiPicker";
import SMSMessageChipEditor, {
  type SMSMessageChipEditorHandle,
} from "../../../shared/sms/SMSMessageChipEditor";
import SMSLengthLabel from "../../../shared/sms/SMSLengthLabel";
import {
  colorTokens,
  mailEditorBodyWorkspaceSx,
  mailEditorPreviewSheetSx,
  mailEditorVariablesZoneSx,
} from "../../../../styles/globalStyles";

const bodySplitRowSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: { xs: 1, md: 1.25 },
  overflow: "hidden",
} as const;

const editColumnSx = {
  flex: { xs: "1 1 auto", md: 1 },
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
} as const;

const sectionHeaderRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  flexShrink: 0,
  mb: 0.5,
  flexWrap: "wrap",
  minWidth: 0,
} as const;

const variablesSectionTitleSx = {
  lineHeight: 1.2,
  mb: 0.35,
  minHeight: "1.1em",
} as const;

const editScrollAreaSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  overflowX: "hidden",
  scrollbarGutter: "stable",
  pr: { xs: 0, sm: 0.25 },
  display: "flex",
  flexDirection: "column",
} as const;

const previewColumnSx = {
  flex: { xs: "none", md: 1 },
  minWidth: 0,
  minHeight: { xs: 260, md: 0 },
  height: { xs: "min(42vh, 360px)", md: "auto" },
  display: "flex",
  flexDirection: "column",
} as const;

const previewHostSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  borderRadius: 6,
  bgcolor: colorTokens.bg.sheetLight,
  p: { xs: 1.25, sm: 1.5 },
} as const;

type SMSEditorBodyWorkspaceProps = {
  template: SMSTemplate | undefined;
  onTemplateChange: (next: SMSTemplate) => void;
  canEdit: boolean;
  placeholders: SMSPlaceholderDefinition[];
  editorRef: React.MutableRefObject<SMSMessageChipEditorHandle | null>;
  previewText: string;
};

export default function SMSEditorBodyWorkspace(props: SMSEditorBodyWorkspaceProps) {
  const { template, onTemplateChange, canEdit, placeholders, editorRef, previewText } = props;

  return (
    <Box sx={mailEditorBodyWorkspaceSx}>
      <Box sx={bodySplitRowSx}>
        <Box sx={editColumnSx}>
          <Box sx={sectionHeaderRowSx}>
            <DarkTypography level="title-sm" colorVariant="legend">
              Édition
            </DarkTypography>
          </Box>

          {canEdit && template && placeholders.length > 0 ? (
            <Box sx={{ ...mailEditorVariablesZoneSx, mb: 0.75 }}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={variablesSectionTitleSx}>
                Variables
              </DarkTypography>
              <SMSPlaceholderChips
                placeholders={placeholders}
                canEdit={canEdit}
                onInsert={(key) => editorRef.current?.insertPlaceholder(key)}
              />
            </Box>
          ) : null}

          <Box sx={editScrollAreaSx}>
            {template ? (
              <>
                <SMSMessageChipEditor
                  ref={editorRef}
                  value={template.body ?? ""}
                  placeholders={placeholders}
                  disabled={!canEdit}
                  minRows={8}
                  maxHeight="100%"
                  onChange={(body) => onTemplateChange({ ...template, body })}
                  placeholder="Message SMS…"
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {canEdit ? (
                    <CuratedEmojiPicker
                      onPick={(emoji) => editorRef.current?.insertText(emoji)}
                      menuZIndex={2500}
                    />
                  ) : (
                    <Box />
                  )}
                  <SMSLengthLabel text={template.body ?? ""} />
                </Box>
              </>
            ) : (
              <DarkTypography level="body-sm" colorVariant="legend">
                Sélectionnez ou créez un modèle
              </DarkTypography>
            )}
          </Box>
        </Box>

        <Box sx={previewColumnSx}>
          <DarkTypography level="title-sm" colorVariant="legend" sx={{ mb: 0.5, flexShrink: 0 }}>
            Aperçu
          </DarkTypography>
          <Sheet variant="outlined" sx={{ ...mailEditorPreviewSheetSx, flex: 1 }}>
            {!template ? (
              <DarkTypography level="body-sm" colorVariant="primary" sx={{ p: 1 }}>
                …
              </DarkTypography>
            ) : (
              <Box sx={previewHostSx}>
                <DarkTypography
                  level="body-md"
                  colorVariant="onLight"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {previewText || " "}
                </DarkTypography>
              </Box>
            )}
          </Sheet>
        </Box>
      </Box>
    </Box>
  );
}
